import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Gift, Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useWalletSafe } from "@/hooks/useWalletSafe";
import { BADGE_IMAGES, BADGE_METADATA, OfficialBadgeType } from "@/hooks/useBadges";

interface ClaimableBadge {
  id: string;
  badge_type: OfficialBadgeType;
  badge_name: string;
  badge_description: string;
  earned_at: string;
}

export function ClaimableBadges() {
  const { connected, publicKey } = useWalletSafe();
  const [claimableBadges, setClaimableBadges] = useState<ClaimableBadge[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState<string | null>(null);

  useEffect(() => {
    if (!connected || !publicKey) {
      setLoading(false);
      return;
    }

    fetchClaimableBadges();

    // Subscribe to realtime updates
    const channel = supabase
      .channel("claimable-badges")
      .on("postgres_changes", { 
        event: "*", 
        schema: "public", 
        table: "user_badges",
        filter: `wallet_address=eq.${publicKey.toBase58()}`
      }, () => {
        fetchClaimableBadges();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [connected, publicKey]);

  const fetchClaimableBadges = async () => {
    if (!publicKey) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("user_badges")
        .select("id, badge_type, badge_name, badge_description, earned_at")
        .eq("user_id", user.id)
        .eq("is_claimed", false)
        .eq("is_active", true);

      if (error) throw error;
      
      // Filter only official badge types
      const officialBadges = (data || []).filter(b => 
        ['early_creator', 'rising_creator', 'power_minter', 'top_seller', 'top_sharer', 'most_liked'].includes(b.badge_type)
      ) as ClaimableBadge[];
      
      setClaimableBadges(officialBadges);
    } catch (err) {
      console.error("Error fetching claimable badges:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (badgeId: string, badgeName: string) => {
    setClaimingId(badgeId);
    
    try {
      const { data, error } = await supabase.rpc("claim_badge", { p_badge_id: badgeId });
      
      if (error) throw error;
      
      if (data) {
        toast({
          title: "Badge Claimed!",
          description: `Congratulations! You've claimed the ${badgeName} badge!`,
        });
        
        // Remove from list
        setClaimableBadges(prev => prev.filter(b => b.id !== badgeId));
      } else {
        toast({
          title: "Claim Failed",
          description: "Unable to claim this badge. Please try again.",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      console.error("Claim error:", err);
      toast({
        title: "Claim Failed",
        description: err.message || "An error occurred",
        variant: "destructive",
      });
    } finally {
      setClaimingId(null);
    }
  };

  // Don't show if no claimable badges
  if (!loading && claimableBadges.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <Card className="border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/20 via-primary/10 to-purple-500/20 shadow-lg shadow-amber-500/20">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden border-2 border-amber-500/60 bg-gradient-to-br from-amber-500/20 via-primary/10 to-purple-500/20 shadow-xl shadow-amber-500/30 animate-pulse-slow">
      {/* Animated glow background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,hsl(45,100%,50%,0.3),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,hsl(280,100%,50%,0.2),transparent_50%)]" />
      
      <CardHeader className="pb-3 relative">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="relative">
            <Sparkles className="w-6 h-6 text-amber-400" />
            <div className="absolute inset-0 animate-ping">
              <Sparkles className="w-6 h-6 text-amber-400 opacity-50" />
            </div>
          </div>
          <span className="text-amber-400 font-bold">New Badges Available!</span>
          <Badge className="bg-amber-500 text-black border-0 font-bold animate-bounce ml-2">
            {claimableBadges.length}
          </Badge>
        </CardTitle>
        <p className="text-sm text-amber-200/80 mt-1">Claim your earned badges now</p>
      </CardHeader>
      
      <CardContent className="space-y-3 relative">
        {claimableBadges.map((badge) => {
          const metadata = BADGE_METADATA[badge.badge_type];
          const imageUrl = BADGE_IMAGES[badge.badge_type];
          
          return (
            <div
              key={badge.id}
              className="flex items-center gap-3 p-4 rounded-xl bg-background/80 border-2 border-amber-500/40 hover:border-amber-400 transition-all hover:shadow-lg hover:shadow-amber-500/20"
            >
              {/* Badge Image */}
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-purple-500 rounded-xl blur-md opacity-60 animate-pulse" />
                <img
                  src={imageUrl}
                  alt={badge.badge_name}
                  className="relative w-16 h-16 rounded-xl object-cover border-2 border-amber-400/50"
                />
              </div>

              {/* Badge Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-foreground text-base">
                  {metadata?.name || badge.badge_name}
                </h4>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {metadata?.description || badge.badge_description}
                </p>
              </div>

              {/* Claim Button - HIGHLY VISIBLE */}
              <Button
                size="lg"
                onClick={() => handleClaim(badge.id, badge.badge_name)}
                disabled={claimingId === badge.id}
                className="gap-2 bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-400 hover:from-amber-400 hover:via-amber-300 hover:to-yellow-300 text-black font-bold shadow-lg shadow-amber-500/40 hover:shadow-xl hover:shadow-amber-400/50 transition-all hover:scale-105 flex-shrink-0 min-w-[120px]"
              >
                {claimingId === badge.id ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Claiming...
                  </>
                ) : (
                  <>
                    <Gift className="w-5 h-5" />
                    CLAIM
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
